import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Archive, Crown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { TIER_LIMITS } from '@/lib/tier-utils';
import { BETA_CONFIG } from '@/config/beta';
import { syncFoundingMemberGoals } from '@/lib/founding-member-sync';

interface Goal {
  id: string;
  title: string;
}

interface Habit {
  id: string;
  title: string;
  goalTitle?: string;
}

interface DowngradeModalProps {
  isOpen: boolean;
  allGoals: Goal[];
  allHabits: Habit[];
  onComplete: () => void;
  onUpgrade: () => void;
}

const MAX_GOALS = TIER_LIMITS.free.max_goals;
const MAX_HABITS = TIER_LIMITS.free.max_habits;

export function DowngradeModal({
  isOpen,
  allGoals,
  allHabits,
  onComplete,
  onUpgrade,
}: DowngradeModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedGoalIds, setSelectedGoalIds] = useState<Set<string>>(
    new Set(allGoals.slice(0, MAX_GOALS).map((g) => g.id))
  );
  const [selectedHabitIds, setSelectedHabitIds] = useState<Set<string>>(
    new Set(allHabits.slice(0, MAX_HABITS).map((h) => h.id))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const needsGoalSelection = allGoals.length > MAX_GOALS;
  const needsHabitSelection = allHabits.length > MAX_HABITS;

  const canConfirm = useMemo(() => {
    const goalsValid = !needsGoalSelection || selectedGoalIds.size === MAX_GOALS;
    const habitsValid = !needsHabitSelection || selectedHabitIds.size === MAX_HABITS;
    return goalsValid && habitsValid;
  }, [selectedGoalIds.size, selectedHabitIds.size, needsGoalSelection, needsHabitSelection]);

  // Never show during beta
  if (BETA_CONFIG.isBetaMode) {
    return null;
  }

  const toggleGoal = (goalId: string) => {
    setSelectedGoalIds((prev) => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else if (next.size < MAX_GOALS) {
        next.add(goalId);
      }
      return next;
    });
  };

  const toggleHabit = (habitId: string) => {
    setSelectedHabitIds((prev) => {
      const next = new Set(prev);
      if (next.has(habitId)) {
        next.delete(habitId);
      } else if (next.size < MAX_HABITS) {
        next.add(habitId);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);

    try {
      // Archive unselected goals
      const goalsToArchive = allGoals
        .filter((g) => !selectedGoalIds.has(g.id))
        .map((g) => g.id);

      if (goalsToArchive.length > 0) {
        const { error: goalsError } = await supabase
          .from('goals')
          .update({ archived: true })
          .in('id', goalsToArchive);

        if (goalsError) throw goalsError;
      }

      // Archive unselected habits
      const habitsToArchive = allHabits
        .filter((h) => !selectedHabitIds.has(h.id))
        .map((h) => h.id);

      if (habitsToArchive.length > 0) {
        const { error: habitsError } = await supabase
          .from('habits')
          .update({ archived: true })
          .in('id', habitsToArchive);

        if (habitsError) throw habitsError;
      }

      // Sync archived goals to founding member progress (fire-and-forget)
      if (goalsToArchive.length > 0 && user?.id) {
        syncFoundingMemberGoals(user.id);
      }

      toast({
        title: 'Selection saved',
        description: `${goalsToArchive.length} goals and ${habitsToArchive.length} habits archived.`,
      });

      onComplete();
    } catch (error) {
      console.error('Error archiving items:', error);
      toast({
        title: 'Error',
        description: 'Failed to save your selection. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-2 text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            <DialogTitle>Your Plan Has Changed</DialogTitle>
          </div>
          <DialogDescription>
            You're now on the Free plan. To continue, please choose which goals and
            habits to keep active.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Goals Selection */}
          {needsGoalSelection && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  Goals (keep {MAX_GOALS} of {allGoals.length})
                </h3>
                <span
                  className={`text-sm font-medium ${
                    selectedGoalIds.size === MAX_GOALS
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  }`}
                >
                  Selected: {selectedGoalIds.size}/{MAX_GOALS}
                </span>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                {allGoals.map((goal) => {
                  const isSelected = selectedGoalIds.has(goal.id);
                  const isDisabled = !isSelected && selectedGoalIds.size >= MAX_GOALS;

                  return (
                    <label
                      key={goal.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : isDisabled
                          ? 'border-muted bg-muted/30 opacity-50 cursor-not-allowed'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onCheckedChange={() => toggleGoal(goal.id)}
                      />
                      <span className="text-sm truncate">{goal.title}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {needsGoalSelection && needsHabitSelection && <Separator />}

          {/* Habits Selection */}
          {needsHabitSelection && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  Habits (keep {MAX_HABITS} of {allHabits.length})
                </h3>
                <span
                  className={`text-sm font-medium ${
                    selectedHabitIds.size === MAX_HABITS
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  }`}
                >
                  Selected: {selectedHabitIds.size}/{MAX_HABITS}
                </span>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                {allHabits.map((habit) => {
                  const isSelected = selectedHabitIds.has(habit.id);
                  const isDisabled = !isSelected && selectedHabitIds.size >= MAX_HABITS;

                  return (
                    <label
                      key={habit.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : isDisabled
                          ? 'border-muted bg-muted/30 opacity-50 cursor-not-allowed'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onCheckedChange={() => toggleHabit(habit.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm truncate block">{habit.title}</span>
                        {habit.goalTitle && (
                          <span className="text-xs text-muted-foreground truncate block">
                            Goal: {habit.goalTitle}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <Separator />

          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
            <Archive className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>
              Unselected items will be archived. You can view them but not edit or
              track them until you upgrade.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Saving...' : 'Confirm Selection'}
          </Button>

          <Separator />

          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">
              Want to keep everything?
            </p>
            <Button
              variant="outline"
              onClick={onUpgrade}
              className="gap-2"
            >
              <Crown className="h-4 w-4" />
              Upgrade to Pro
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
